import { useCallback, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useLang } from "@/i18n";

export const Lightbox = ({ items, index, onClose, onChange }) => {
  const { t } = useLang();
  const open = index !== null && index !== undefined;
  const go = useCallback((d) => onChange((index + d + items.length) % items.length), [index, items.length, onChange]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    window.__lenis?.stop();
    return () => { window.removeEventListener("keydown", onKey); window.__lenis?.start(); };
  }, [open, go, onClose]);

  const item = open ? items[index] : null;
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          role="dialog" aria-modal="true" aria-label={item?.caption}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] bg-[rgba(12,30,21,0.94)] backdrop-blur-md flex flex-col"
          onClick={onClose}
          data-testid="lightbox"
        >
          <div className="flex justify-between items-center p-4 sm:p-6 text-white">
            <span className="text-sm tracking-widest" data-testid="lightbox-counter">{index + 1} / {items.length}</span>
            <button onClick={onClose} aria-label={t.gallery.close} className="h-11 w-11 grid place-items-center rounded-full bg-white/10 hover:bg-white/20" data-testid="lightbox-close"><X size={22} /></button>
          </div>
          <div className="relative flex-1 flex items-center justify-center px-4 sm:px-20 min-h-0" onClick={(e) => e.stopPropagation()}>
            <motion.img
              key={item.src}
              src={item.src}
              alt={item.caption}
              initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.45 }}
              className="max-h-full max-w-full object-contain rounded-xl bg-white"
              data-testid="lightbox-image"
            />
            <button onClick={() => go(-1)} aria-label={t.gallery.prev} className="absolute left-2 sm:left-6 h-12 w-12 grid place-items-center rounded-full bg-white text-[var(--ink)] hover:bg-[var(--yellow)]" data-testid="lightbox-prev"><ChevronLeft /></button>
            <button onClick={() => go(1)} aria-label={t.gallery.next} className="absolute right-2 sm:right-6 h-12 w-12 grid place-items-center rounded-full bg-white text-[var(--ink)] hover:bg-[var(--yellow)]" data-testid="lightbox-next"><ChevronRight /></button>
          </div>
          <p className="text-center text-white/90 px-6 py-5 font-display italic text-lg" data-testid="lightbox-caption">{item.caption}</p>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
